<template>
  <div data-page-name="events">
    <h1>{{ $t('Events') }}</h1>

    <div class="filters">
      <div class="left">
        <AppDropdown.Base
          v-model="format"
          :label="$t('Event_Format')"
          :options="FORMATS"
          key-attribute="label"
          fill="outline"
        />

        <AppDropdown.Base
          v-model="location"
          :label="$t('Location')"
          :options="LOCATIONS"
          fill="outline"
          key-attribute="label"
        />

        <AppCalendar
          v-model:visible-range="visibleRange"
          v-model:from="queries.date_from"
          v-model:to="queries.date_to"
          :highlighted-dates="datesWithEvents"
        />
      </div>

      <div class="right">
        <Buttons.Regular
          :disabled="!someFilterApplied"
          fill="none"
          accent="black"
          class="nowrap"
          @click="resetFilters"
        >
          {{ $t('Clear_all_filters') }}
          <SvgIcon name="close" fill="inherit" />
        </Buttons.Regular>
      </div>
    </div>

    <div class="content">
      <ul class="articles">
        <li v-for="event of events" :key="event._id">
          <CardEvent :event />
        </li>
      </ul>

      <AppSubscribe />
    </div>

    <AppPagination
      v-if="Number(meta.lastPage) > 1"
      v-model="queries.page"
      :total-items="meta.total"
      :items-per-page="meta.perPage"
      :max-pages-shown="4"
    />
  </div>
</template>

<script setup lang="ts">
// components
import AppDropdown from '~/components/redesign/AppDropdown';
import AppCalendar from '~/components/redesign/AppCalendar.vue';
import Buttons from '~/components/redesign/Buttons';
import CardEvent from '~/components/redesign/CardEvent.vue';
import AppSubscribe from '~/components/AppSubscribe.vue';
import AppPagination from '~/components/redesign/AppPagination.vue';

// composables
import { useI18n } from '#i18n';
import { useDayjs } from '#dayjs';
import useApiSeo from '~/composables/useApiSeo';

// utils
import get from 'lodash.get';
import omit from 'lodash.omit';
import scrollToTop from '~/utils/scrollToTop';
import getMonthRange from '~/utils/getMonthRange';

// types
import type { Range } from '~/utils/getMonthRange';

// constants
import {
  EventRegion,
  EventStatus,
  EventType,
  EventFormat,
} from '~/types/events';

type Format = {
  query?: EventFormat;
  label: string;
  value?: {
    type?: EventType;
    status: EventStatus;
  };
};
type Location = { label: string; query?: EventRegion };

const { t } = useI18n();
const dayjs = useDayjs();
const router = useRouter();
const { searchParams } = useRequestURL();

const FORMATS: Format[] = [
  {
    query: undefined,
    label: t('All'),
    value: undefined,
  },
  {
    query: EventFormat.IN_PERSON,
    label: t('In_Person'),
    value: { type: EventType.OFFLINE, status: EventStatus.UPCOMING },
  },
  {
    query: EventFormat.ONLINE,
    label: t('Online'),
    value: { type: EventType.ONLINE, status: EventStatus.UPCOMING },
  },
  {
    query: EventFormat.ON_DEMAND,
    label: t('On_Demand'),
    value: { type: EventType.ONLINE, status: EventStatus.ON_DEMAND },
  },
  {
    query: EventFormat.ONGOING,
    label: t('Ongoing'),
    value: { type: undefined, status: EventStatus.ONGOING },
  },
];
const LOCATIONS: Location[] = [
  {
    query: undefined,
    label: t('Global'),
  },
  {
    query: EventRegion.NORTH_AMERICA,
    label: t('North_America'),
  },
  {
    query: EventRegion.LATAM,
    label: t('LATAM'),
  },
  {
    query: EventRegion.EMEA,
    label: t('EMEA'),
  },
];

const queries = reactive({
  page: Number(searchParams.get('page')) || 1,
  format: searchParams.get('format') || undefined,
  location: searchParams.get('location') || undefined,
  date_from: searchParams.get('date_from') || undefined,
  date_to: searchParams.get('date_to') || undefined,
});

const someFilterApplied = computed(() =>
  Object.values(omit(queries, 'page')).some(Boolean),
);

const resetFilters = () => {
  for (const key in queries) {
    queries[key] = key === 'page' ? 1 : undefined;
  }
};

const format = computed<Format>({
  get: () =>
    FORMATS.find(({ query }) => query === queries.format) || FORMATS[0],
  set: ({ query }) => {
    queries.format = query;
  },
});
const location = computed<Location>({
  get: () =>
    LOCATIONS.find(({ query }) => query === queries.location) || LOCATIONS[0],
  set: ({ query }) => {
    queries.location = query;
  },
});

const { data } = await useAsyncGql({
  operation: 'GetEvents',
  variables: {
    ...omit(toRefs(queries), 'format'),

    // fixme: is it possible to avoid using computed?
    type: computed(() => format.value.value?.type),
    status: computed(() => format.value.value?.status),
  },
});

const events = computed(() => data.value?.events.data || []);
const meta = computed(() => data.value?.events.paginatorInfo || {});

watch(Object.values(omit(toRefs(queries), 'page')), () => {
  queries.page = 1;
});

watch(queries, async query => {
  await router.push({ query });
  scrollToTop();
});

//

const visibleRange = ref<Range>(getMonthRange());
const visibleRangeKey = computed(() => visibleRange.value.from.substring(0, 7));

const requestedRangesKeys = new Set();
const datesWithEvents = reactive<Date[]>([]);

const requestEventsByRange = async ({ from, to }: Range) => {
  const response = await GqlGetEventsDates({ date_from: from, date_to: to });

  const uniqueDates = get(response, 'events.data', [])
    .flatMap((dates: Record<'event_date' | 'finish_at', string>) => {
      let [from, to] = Object.values(dates).map(date => date.substring(0, 10));

      if (dayjs(from).isSame(to, 'day')) return [from, to];

      if (!dayjs(from).isBefore(to)) {
        const temp = from;

        from = to;
        to = temp;
      }

      const datesBetweenRange = [from];
      let daysCountBetweenRange = Math.abs(dayjs(from).diff(to, 'days')) - 1;

      while (daysCountBetweenRange > 0) {
        const date = dayjs(from)
          .add(daysCountBetweenRange, 'days')
          .format('YYYY-MM-DD');

        datesBetweenRange.push(date);
        daysCountBetweenRange--;
      }

      datesBetweenRange.push(to);

      return datesBetweenRange;
    })
    .reduce((acc, date) => acc.add(date), new Set());

  return Array.from(uniqueDates).map(date =>
    dayjs(date).set('hour', 0).set('minute', 0).set('millisecond', 0).toDate(),
  );
};

watch(visibleRange, async range => {
  if (requestedRangesKeys.has(visibleRangeKey.value)) return;

  datesWithEvents.push(...(await requestEventsByRange(range)));
  requestedRangesKeys.add(visibleRangeKey.value);
});

useApiSeo('events', 'Page');
</script>

<style scoped lang="scss">
@import '$/mixins/flex';
@import '$/mixins/size';
@import '$/mixins/media';
@import '$/mixins/typography';
@import '$/functions/token';
@import '$/variables/fonts';

[data-page-name='events'] {
  @include wrapper(80rem);
  padding: 6rem 2rem;

  @include tablet {
    padding: 3rem 2rem;
  }

  @include mobile {
    padding: 1.5rem 1rem;
  }

  h1 {
    @include h2;
    margin-bottom: 3rem;

    @include tablet {
      margin-bottom: 2.5rem;
    }

    @include mobile {
      margin-bottom: 2rem;
    }
  }

  .filters {
    @include flex-center-sb;
    gap: 0 2rem;

    background-color: token('surf-cont-secondary');
    margin-bottom: 2rem;

    padding: 1rem;

    @include tablet {
      flex-direction: column;
      gap: 1rem 0;
    }

    @include mobile {
      gap: 1.5rem 0;
    }

    .left {
      width: 100%;

      @include flex-center-start;
      gap: 0 2rem;

      @include tablet {
        gap: 1.5rem;
      }

      @include mobile {
        flex-direction: column;
      }

      [data-component-name='AppDropdown.Base'] {
        @include fluid-max-width(12.5rem);

        @include tablet {
          @include fluid-max-width(30%);
        }

        @include mobile {
          @include fluid-max-width(100%);
        }
      }

      [data-component-name='AppCalendar'] {
        @include fluid-max-width(16rem);

        @include tablet {
          @include fluid-max-width(100%);
        }
      }
    }

    .right {
      @include tablet {
        align-self: flex-end;
      }

      @include mobile {
        align-self: center;
      }
    }
  }

  .content {
    @include flex-start-sb;
    gap: 2rem;

    @include tablet {
      flex-direction: column;
    }

    ul.articles {
      @include flex-stretch-sb;
      flex-direction: column;
      gap: 2rem;

      width: 100%;
    }

    [data-component-name='AppSubscribe'] {
      width: 24rem;

      @include tablet {
        width: 100%;
      }
    }
  }

  [data-component-name='AppPagination'] {
    margin-top: 5rem;

    @include tablet {
      margin-top: 2rem;
    }

    @include mobile {
      margin-top: 1.5rem;
    }
  }
}
</style>
